<template>
    <div>
        <div class="pb-5 pt-8 px-6">
            <div class="flex items-center justify-start gap-4">
                <AppButton
                    @click="closeTray" 
                    icon="ChevronLeft"
                    icon-only="true"
                    aria-label="Close"
                </AppButton>
                <span class="font-medium text-lg leading-tight text-v2-gray-20 dark:text-v2-white">
                    {{ drawer.selected_tray?.name }}
                    <span class="font-medium text-v2-gray-80 text-sm dark:text-v2-gray-90">({{drawer.selected_tray?.pin_count ?? 0}})</span>
                </span>
                <span class="ml-auto item-end">
                    <AppButton 
                        @click="forceClose" 
                        icon="close"
                        icon-only="true"
                        aria-label="Close"
                    />
                </span>
            </div>
            <!-- Optional tray description. -->
            <div v-if="drawer.selected_tray.name !== 'Favorites'">
                <p
                    @click="editDescription"
                    v-if="!isEditingDescription"
                    :class="drawer.selected_tray?.description?.length ? '' : 'italic'"
                    class="group relative flex items-center duration-150 rounded px-1 py-px hover:bg-gray-tier-6 hover:type-primary w-fit -ml-1 leading-snug mt-1 text-v2-gray-80 text-sm"
                >
                    {{ drawer.selected_tray?.description || "Add a short description" }}
                    <ButtonTooltip class="hidden group-hover:block not-italic" text="Update"  />
                </p>
                <div v-click-outside="cancel" v-else class="relative">
                    <input
                        ref="newDescriptionRef"
                        type="text"
                        @keyup.enter="updateDescription"
                        @keyup.stop.esc="cancel"
                        v-model="newDescriptionInput"
                        class="w-full flex mt-1 -ml-1 px-1 py-px rounded text-sm border-none outline-none focus-within:ring-0 bg-gray-tier-6"
                    />
                    <div class="bg-white p-2 rounded-lg shadow-lg top-full mt-1 absolute flex items-center gap-2">
                        <AppButton @click="cancel" size="small" icon="close" text="Cancel" />
                        <AppButton @click="updateDescription" theme="green" size="small" icon="check" text="Save" />
                    </div>
                </div>
            </div>
        </div>
        <div class="border-t border-v2-gray-110 flex flex-col gap-2 py-5 px-6 at1440:flex-row dark:border-v2-gray-10">
            <SearchInput
                v-model="computedSearchInput"
                placeholder="Search this Tray"
                class="grow bg-v2-gray-130 border-v2-gray-110 dark:bg-v2-gray-30 dark:border-v2-gray-10  !h-[34px]"
                :class="{'opacity-50 pointer-events-none': !drawer.pins.length}"
                :large="true"
                :disabled="!drawer.pins.length"
            />
            <SeclockMultiselect
                :disabled="!searchHasResults || !drawer.pins.length"
                v-model="computedSortValue"
                :options="searchSortOptions"
                :can-deselect="false"
                :option-is-object="true"
                :label="$t('Sort')"
                class="flex !w-full"
                :class="{'opacity-50 pointer-events-none': !searchHasResults || !drawer.pins.length}"
                label-position="side"
                :contrast="true"
                :hideSelected="true"
            />
        </div>
        <Divider />
    </div>
</template>

<script setup>
import {AppButton, BaseSvg, ButtonTooltip, SearchInput, SeclockMultiselect, Divider} from "@/Components";
import {useDialog, usePinsDrawer, usePinSearch} from "@/Composables";
import {computed, nextTick, ref} from "vue";

const { drawer, closeTray, updateTray } = usePinsDrawer();
const { forceClose } = useDialog()

const { searchHasResults, searchSortOptions } = usePinSearch();

const props = defineProps(['modelValue', 'sortValue']);
const emit = defineEmits(["update:modelValue", 'update:sortValue']);

const computedSearchInput = computed({
    get: () => props.modelValue,
    set: (value) => emit("update:modelValue", value),
});

const computedSortValue = computed({
    get: () => props.sortValue,
    set: (value) => emit("update:sortValue", value),
})

// Description Editting
const newDescriptionRef = ref();
const newDescriptionInput = ref("");
const isEditingDescription = ref(false);
function editDescription () {
    newDescriptionInput.value = drawer.value?.selected_tray?.description;
    isEditingDescription.value = true;
    nextTick(() => {
        newDescriptionRef.value?.focus();
    });
}

function updateDescription () {
    updateTray(drawer.value.selected_tray.id, drawer.value.selected_tray.name, newDescriptionInput.value, drawer.value.selected_tray?.icon);
    isEditingDescription.value = false;
}

function cancel () {
    newDescriptionInput.value = "";
    isEditingDescription.value = false;
}
</script>
